import Vue from 'vue';
import { mapGetters } from 'vuex';
import { Sketch } from 'vue-color';
import _ from 'lodash';

export default Vue.component('shadow-set', {
  template: require('./template.html'),
  name: 'shadow-set',
  components: {
    'sketch-picker': Sketch,
  },
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    schemeColors() {
      return [{ color: '', type: 'picker' }];
    },
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
  },
  props: [
    'option',
    'active-state',
    'dom-nodes',
    'component-config',
  ],
  data() {
    return {
      display: {
        boxshadow: {
          horizontal: 0,
          horizontal_unit: 'px',
          vertical: 0,
          vertical_unit: 'px',
          blur: 0,
          blur_unit: 'px',
          spread: 0,
          spread_unit: 'px',
          type: '',
        },
      },
      debTime: 250,
      innerDomNodes: {},
      colorPickers: {
        shadowColor: false,
      },
      pickerVals: {
        shadowColor: {},
      },
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  created() {
    this.pickerVals.shadowColor = this._defaultColorPickerObj;
    // check for previous values
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const option = this.option;
      const payload = {
        type: option.payload.type,
      };
      // build local $vm
      payload.$vm = {
        viewport: this.viewport,
        droplet: this.activeDroplet,
      };

      const properties = option.payload.properties;
      payload.property = properties[0];

      let selector = option.payload.selector;
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        const inner = this.componentConfig.itemList.selectors[payload.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}`;
      }
      // build selector
      if (option.payload.activeState) {
        payload.selector = this._buildStateSelector({ selector, activeState: this.activeState });
      } else {
        payload.selector = selector;
      }

      if (option.$el) {
        const useRoot = option.$el === 'root';
        const $root = this.domNodes.$dropletRow;
        payload.$el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
      }
      const shadow = this._getValShadow(payload);
      this.display.boxshadow.horizontal = shadow.horizontal.val;
      this.display.boxshadow.horizontal_unit = shadow.horizontal.unit;
      this.display.boxshadow.vertical = shadow.vertical.val;
      this.display.boxshadow.vertical_unit = shadow.vertical.unit;
      this.display.boxshadow.blur = shadow.blur.val;
      this.display.boxshadow.blur_unit = shadow.blur.unit;
      this.display.boxshadow.spread = shadow.spread.val;
      this.display.boxshadow.spread_unit = shadow.spread.unit;
      this.display.boxshadow.type = shadow.type === 'inset';
      this.pickerVals.shadowColor = this._buildPickerVal(shadow.color);
    },
    changeCSS(params) {
      const payload = {};
      payload.drp = this.activeDroplet;
      payload.val = this.buildVal();
      payload.selector = params.selector;
      payload.viewport = this.viewport;
      // we only want to show updates to the dom if in default state
      if (this.activeState === '') {
        this.setLocals({ ...payload, properties: params.properties });
      }
      // slide inputs will always need a debounce
      this._debounce(() => {
        _.forEach(params.properties, (property) => {
          payload.property = property;
          if (this.activeDropletItem._id && this.componentConfig.itemList) {
            // this is in the event a user is targeting a specific item within a droplet
            // eg, a nav link or a column in columns
            const nested = this.componentConfig.itemList.selectors[property] || '';
            payload.selector = `#drp-${this.activeDropletItem._id}${nested}`;
          } else {
            this.resetCustomQueries({ property });
            payload.selector = params.selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
          }
          // draw active state in selector if needed
          if (params.activeState) {
            payload.selector = this._buildStateSelector({
              selector: payload.selector,
              activeState: this.activeState,
            });
          }
          this.$emit('check-legacy-styles', payload);
          this.$store.dispatch('changeDropletCss', payload);
        });
        this.$emit('css-generator', this.cssPayload(), this.option.plugin);
        this.$emit('update-changes', true);
        this.setLocals({ val: null, properties: [] });
      }, this.debTime);
    },
    buildVal() {
      this.display.boxshadow.type = this.display.boxshadow.type ? 'inset' : '';
      return this._buildShadowVal({
        coords: this.display.boxshadow,
        color: this.pickerVals.shadowColor,
        options: this.option.payload,
      });
    },
    toggleColorPicker(type) {
      _.forOwn(this.colorPickers, (value, key) => {
        if (type && key !== type) {
          this.colorPickers[key] = false;
        } else if (!type) {
          this.colorPickers[key] = false;
        }
      });
      if (type) {
        this.colorPickers[type] = !this.colorPickers[type];
      }
    },
    setLocals(params) {
      this.$store.dispatch('setLocalStyles', params);
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    resetCustomQueries(update) {
      if (!this.componentConfig.itemList) {
        return;
      }
      const inner = this.componentConfig.itemList.selectors[update.property];
      const nested = inner || '';
      const customRule = new RegExp(`#drp-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}${nested}`, 'gi');
      _.forEach(this.activeDroplet.mediaQueries, (rule) => {
        if (rule.selector.match(customRule)) {
          const property = _.find(rule.properties, r => r.property === update.property);
          if (property) {
            this.$store.dispatch('setDropletVal', {
              drp: property,
              key: this.viewport,
              val: 'default',
            });
          }
        }
      });
    },
    shouldHide(type) {
      return this.option.payload.hiddenInputs && this.option.payload.hiddenInputs.includes(type);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/shadow-set/index.js